import { useTranslation } from "react-i18next";
import PrimaryButton from "../../../../../components/common/PrimaryButton";
import styles from "./styles.module.scss";
import { useContext, useMemo, useState } from "react";
import { ContractContext } from "../../../../../providers/contract";
import { ECONTRACT_STATUS } from "../../../../../enums";
import classNames from "classnames";
import WaffyFeesSummary from "../components/WaffyFeesSummary";
import { getUserId } from "../../../../../setup/auth/oidc";
import {
  ESenderRole,
  AddOnFeeCategory,
} from "../../../../../setup/api/services/contract/models";
import { EContractActions } from "../../../../../setup/api/services/contractActions/enum";
import {
  doAction,
  getAdminActorRole,
  getSettlementActorRole,
} from "../../../../../setup/api/services/contractActions";
import TextInput, {
  InputType,
} from "../../../../../components/forms/textInput";
import { getShipmentFeesTotal } from "../../../../../setup/util/contract";
import { roundToTwoDecimals } from "../../../../../setup/util/helpers";
import { getUserIdentificationFromContractParties } from "../../../../../setup/util/userIdentifier";
import { getUnregisteredContractParties } from "../../../../../setup/util/partyRegistration";
import UnregisteredPartiesAlert from "../../UnregisteredPartiesAlert";

interface DefaultSettlementProps {
  propose?: boolean;
  onSuccess?: () => void;
}

const DefaultSettlement = ({ propose, onSuccess }: DefaultSettlementProps) => {
  const { t } = useTranslation();
  const { contract, contractActivities, actions } = useContext(ContractContext);

  const wasItemAcceptedByBuyer = useMemo(
    () =>
      !!contractActivities?.find(
        (activity) => activity.to === ECONTRACT_STATUS.ITEM_ACCEPTED
      ),
    [contractActivities]
  );

  const [loading, setLoading] = useState(false);
  const [naqelFees, setNaqelFees] = useState<number | undefined>(0);
  const [naqelFeesDisplay, setNaqelFeesDisplay] = useState<string>("0");

  const userId = getUserId();

  const chargedShipmentFee = getShipmentFeesTotal(contract);

  const shippingFeeAmount = useMemo(() => {
    if (
      contract?.shipment?.feeBreakdown?.settled &&
      chargedShipmentFee &&
      contract?.shipment?.feeBreakdown?.settled != chargedShipmentFee
    ) {
      return roundToTwoDecimals(contract?.shipment?.feeBreakdown?.settled);
    }

    if (!contract?.addonFeesList?.length) return 0;

    return roundToTwoDecimals(chargedShipmentFee) ?? 0;
  }, [
    contract?.addonFeesList,
    contract?.shipment?.feeBreakdown?.settled,
    chargedShipmentFee,
  ]);

  const waffyExtraFeesAmount = useMemo(() => {
    if (naqelFees === undefined || shippingFeeAmount === 0) {
      return roundToTwoDecimals(shippingFeeAmount);
    }
    const fees = roundToTwoDecimals(naqelFees) || 0;
    const remaining =
      roundToTwoDecimals(shippingFeeAmount) - roundToTwoDecimals(fees);
    return remaining >= 0 ? roundToTwoDecimals(remaining) : 0;
  }, [naqelFees, shippingFeeAmount]);

  const userToSettle = useMemo(() => {
    if (
      !wasItemAcceptedByBuyer &&
      contract?.status === ECONTRACT_STATUS.READY_FOR_CASH_OUT
    ) {
      return "buyer";
    }

    if (
      contract?.status === ECONTRACT_STATUS.ITEM_ACCEPTED ||
      contract?.status === ECONTRACT_STATUS.WAITING_FOR_SETTLEMENT_CLOSE ||
      contract?.status === ECONTRACT_STATUS.READY_FOR_CASH_OUT
    ) {
      return "seller";
    }
    return "buyer";
  }, [contract, wasItemAcceptedByBuyer]);

  const totalAmountDue = useMemo(() => {
    return userToSettle === "seller" && contract?.originalItemPrice
      ? roundToTwoDecimals(contract?.originalItemPrice) ?? 0
      : roundToTwoDecimals(contract?.itemPrice) ?? 0;
  }, [contract, userToSettle]);

  const userAmountDue = useMemo(() => {
    if (
      contract?.shipment?.feeBreakdown?.settled &&
      chargedShipmentFee &&
      contract?.shipment?.feeBreakdown?.settled != chargedShipmentFee
    ) {
      const delta =
        contract?.shipment?.feeBreakdown?.settled - chargedShipmentFee;
      return roundToTwoDecimals(totalAmountDue - delta);
    }

    const isOldShipmentFee =
      !contract?.addonFeesList?.length && contract?.shipment?.fee;

    const oldExtraShipmentFee =
      (isOldShipmentFee ? contract?.shipment?.fee : 0) ?? 0;
    return roundToTwoDecimals(totalAmountDue + oldExtraShipmentFee);
  }, [
    contract?.shipment?.feeBreakdown,
    contract?.addonFeesList?.length,
    contract?.shipment?.fee,
    chargedShipmentFee,
    totalAmountDue,
  ]);

  const totalAmountDueWithFee = useMemo(() => {
    return roundToTwoDecimals(totalAmountDue + getShipmentFeesTotal(contract));
  }, [contract, totalAmountDue]);

  const contractFeesAmount = useMemo(() => {
    const flatValue = contract?.feePercentage
      ? contract?.feePercentage * totalAmountDueWithFee
      : contract?.fee;
    const roundedTo2Decimals = flatValue ? roundToTwoDecimals(flatValue) : 0;
    return roundToTwoDecimals(roundedTo2Decimals);
  }, [contract?.fee, contract?.feePercentage, totalAmountDueWithFee]);

  const activeEscalation = useMemo(
    () => contract?.escalations?.find((e) => !e.resolved),
    [contract?.escalations]
  );

  // a settlement cannot be sent for a party the backend cannot resolve to a
  // real user, so every party has to be registered with a valid id first
  const unregisteredParties = useMemo(
    () => getUnregisteredContractParties(contract),
    [contract]
  );

  const settlementBlocked = loading || unregisteredParties.length > 0;

  const handleSettleContract = async () => {
    if (!contract) return;
    if (!userId) return;
    if (unregisteredParties.length) return;

    try {
      setLoading(true);

      if (propose) {
        const settleUserIdentification =
          getUserIdentificationFromContractParties(
            contract,
            userToSettle === "seller"
              ? ESenderRole.PROVIDER
              : ESenderRole.CUSTOMER
          );

        const resolution =
          userToSettle === "seller" ? "RESOLVED_PROVIDER" : "RESOLVED_CUSTOMER";

        const brokerUserIdentification = contract?.parties?.find(
          (p) => p.role == ESenderRole.BROKER
        )?.userId;

        await doAction({
          contractId: contract.id,
          userId: String(userId),
          actorRole: getAdminActorRole(),
          contractAction: EContractActions.ADMIN_RESOLVE_ESCALATION,
          escalationDTO: {
            resolution,
            proposedSettlement: [
              {
                id: settleUserIdentification,
                amountDue: userAmountDue - (contract?.fee ?? 0),
              },
              brokerUserIdentification && {
                id: String(brokerUserIdentification),
                amountDue: contract?.fee ?? 0,
              },
            ],
          },
        } as any);
        actions.getContract();
        onSuccess?.();
      } else {
        const waffyExtraFees =
          waffyExtraFeesAmount && contract?.shippable
            ? [
                {
                  amount: waffyExtraFeesAmount,
                  description: null,
                  type: AddOnFeeCategory.SHIPPING_FEES,
                },
              ]
            : undefined;

        const naqelServiceProvider = contract?.parties?.find(
          (party) => party.role === ESenderRole.SERVICE_PROVIDER
        );
        const naqelCashoutAmountItem =
          naqelServiceProvider?.userId && naqelFees
            ? {
                id: Number(naqelServiceProvider?.userId),
                amountDue: naqelFees,
                feePercentage: 0,
              }
            : undefined;

        const settleIdentification = getUserIdentificationFromContractParties(
          contract,
          userToSettle === "seller"
            ? ESenderRole.PROVIDER
            : ESenderRole.CUSTOMER
        );

        await doAction({
          contractId: contract?.id,
          userId: userId,
          actorRole: getSettlementActorRole(),
          senderId: String(contract?.senderId),
          contractAction: EContractActions.ADMIN_SETTLE_CONTRACT,
          cashOutAmountList: [
            {
              id: settleIdentification,
              amountDue: userAmountDue,
              fee: contractFeesAmount ?? 0,
            },
            ...(naqelCashoutAmountItem ? [naqelCashoutAmountItem] : []),
          ],
          ...(waffyExtraFees && { waffyExtraFees }),
        } as any);
        window.location.reload();
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <WaffyFeesSummary
          feeAmount={contractFeesAmount ?? 0}
          remainingShippingFeeAmount={
            roundToTwoDecimals(waffyExtraFeesAmount) ?? 0
          }
        />

        <h2 className={classNames(styles["subtitle"], styles["h2"])}>
          {userToSettle === "seller" ? t("lbl_the_seller") : t("lbl_the_buyer")}
        </h2>
        <div className={styles.table_container}>
          <div className={styles.table_item}>
            <div className={styles.table_item_content}>
              <p className={classNames(styles["g600"], styles["subtitle2"])}>
                {t("lbl_settlement_total_amount")}
              </p>

              <h2 className={classNames(styles["g700"], styles["h2"])}>
                {`${totalAmountDueWithFee.toFixed(2)} ${t(
                  "lbl_saudi_currency"
                )}`}
              </h2>
            </div>
          </div>
          <div className={styles.vertical_line}></div>
          <div className={styles.table_item}>
            <div className={styles.table_item_content}>
              <p className={classNames(styles["g600"], styles["subtitle2"])}>
                {t("lbl_more_details_sheet_fees")}
              </p>
              <h2 className={classNames(styles["g700"], styles["h2"])}>
                {`${(contract?.feePercentage || 0) * 100}% - ${(
                  contractFeesAmount ?? 0
                ).toFixed(2)}${t("lbl_saudi_currency")}
                `}
              </h2>
            </div>
          </div>
          <div className={styles.vertical_line}></div>
          <div className={styles.table_item}>
            <div className={styles.table_item_content}>
              <p className={classNames(styles["g600"], styles["subtitle2"])}>
                {t("lbl_deserved_amount")}
              </p>

              <h2 className={classNames(styles["g700"], styles["h2"])}>
                {`${userAmountDue} ${t("lbl_saudi_currency")}`}
              </h2>
            </div>
          </div>
          <div className={styles.vertical_line}></div>
          <div className={styles.table_item}>
            <div className={styles.table_item_content}>
              <p className={classNames(styles["g600"], styles["subtitle2"])}>
                {t("lbl_loyal_receive")}
              </p>
              <h2 className={classNames(styles["doneText"], styles["h2"])}>
                {(userAmountDue - (contractFeesAmount ?? 0)).toLocaleString(
                  undefined,
                  {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  }
                ) +
                  " " +
                  t("lbl_saudi_currency")}
              </h2>
            </div>
          </div>
        </div>
        {contract?.shippable && getShipmentFeesTotal(contract) > 0 && (
          <div className={styles.sub_section}>
            <h2 className={classNames(styles["subtitle"], styles["h2"])}>
              {t("lbl_shipping_company")}
            </h2>
            <div className={styles.total_amount_container}>
              <TextInput
                label={t("lbl_amount_received")}
                maxValue={shippingFeeAmount}
                type={InputType.NUMBER}
                handleChange={(e) => {
                  const raw = e.target.value;
                  setNaqelFeesDisplay(raw);
                  setNaqelFees(raw === "" ? undefined : Number(raw));
                }}
                value={naqelFeesDisplay}
                description={t("lbl_saudi_currency")}
                preventMaxValuePass
                outerContainerWidth="100%"
                containerWidth="100%"
                placeholder="0.0"
              />
            </div>
          </div>
        )}
      </div>
      <UnregisteredPartiesAlert parties={unregisteredParties} />
      <div className={styles.cta_container}>
        <PrimaryButton
          style={{
            backgroundColor: settlementBlocked ? "#9FA6AB" : "#0D44AD",
            height: "48px",
            color: "#FFFFFF",
            cursor: settlementBlocked ? "not-allowed" : "pointer",
            fontSize: "1rem",
            fontWeight: 600,
          }}
          onClick={handleSettleContract}
          disabled={settlementBlocked}>
          {propose ? t("lbl_propose_settlement") : t("lbl_confirm_settlement")}
        </PrimaryButton>
      </div>
    </div>
  );
};

export default DefaultSettlement;
